import clsx from 'clsx';

export type ButtonColor = 'primary' | 'secondary' | 'gray' | 'white' | 'red';

export interface ButtonProps {
  className?: string;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  secondary?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  color?: ButtonColor;
  // eslint-disable-next-line react/no-unused-prop-types
  size?: 'round' | 'large' | 'medium' | 'small';
  type?: 'button' | 'submit' | 'reset';
}

function getButtonColor({ secondary, color }: ButtonProps): ButtonColor {
  if (secondary) {
    return 'secondary';
  }
  return color || 'primary';
}

function getButtonColorClass(props: ButtonProps): string {
  const { disabled, color } = props;
  if (disabled) {
    return 'border-transparent text-gray-500 bg-gray-300';
  }
  switch (getButtonColor(props)) {
    case 'primary':
      return 'border-transparent text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    case 'secondary':
      return 'border-transparent text-white bg-gray-800 hover:bg-gray-900 hover:bg-gray-800 focus:ring-gray-700';
    case 'gray':
      return 'border-transparent text-gray-800 bg-gray-100 hover:bg-gray-200 focus:ring-gray-700';
    case 'white':
      return 'border-1 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500';
    case 'red':
      return 'border-transparent bg-red-600 text-sm font-medium text-white ';
    default:
      throw new Error(`Unknown color: ${color as string}`);
  }
}

function getButtonSizeClass({ size = 'medium' }: ButtonProps): string {
  switch (size) {
    case 'round':
      return 'px-4 py-2 leading-4 rounded-full shadow-sm';
    case 'large':
      return 'px-6 py-3 shadow-sm rounded-md';
    case 'medium':
      return 'px-6 py-2 rounded-md';
    case 'small':
      return 'px-2.5 py-1.5 rounded-sm';
    default:
      throw new Error(`Unknown size: ${size as string}`);
  }
}

function Button(props: ButtonProps): JSX.Element {
  const { className, children, disabled, type = 'button', onClick } = props;
  return (
    <button
      className={clsx(
        'border text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2',
        getButtonSizeClass(props),
        getButtonColorClass(props),
        { 'cursor-not-allowed': disabled },
        className,
      )}
      disabled={disabled}
      onClick={onClick}
      // a type is being provided but eslint doesn't know
      // eslint-disable-next-line react/button-has-type
      type={type}
    >
      {children}
    </button>
  );
}

export default Button;
